import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';

import { deleteBill } from '../../api';
import { BILLS_QUERY_KEY } from '../../hooks';
import { BillListItem } from '../../types';

interface DeleteBillModalProps {
  bill: BillListItem | null;
  onClose: () => void;
}

export const DeleteBillModal = ({ onClose, bill }: DeleteBillModalProps) => {
  const { t } = useTranslation('authenticated.bills', { keyPrefix: 'modals.deleteBill' });

  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: deleteBill,
    onError: () => {},
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      onClose();
    },
  });

  if (!bill) return null;

  return (
    <ConfirmationModal
      title={t('title')}
      message={
        <div>
          <Trans
            t={t}
            i18nKey="description"
            values={{
              billNumber: '#' + bill.number,
            }}
            components={{ bold: <strong />, br: <br /> }}
          />
          <Alert variant={'warning'} className="mt-2">
            <AlertTriangle className="h-5 w-5" />
            <AlertDescription>{t('warning')}</AlertDescription>
          </Alert>
        </div>
      }
      confirmText={t('submit')}
      open={!!bill}
      isLoading={isPending}
      onClose={onClose}
      onConfirm={() => mutate({ billId: bill.id })}
    />
  );
};
